import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, getOr, every } from 'lodash/fp';
import {
  Grid,
  Row,
  Col,
  Icon,
  Input,
  DateRangePicker,
  CheckPicker,
  Button
} from 'rsuite';

import { viewAccess } from '../../../helpers/permission';
import {
  ItemType,
  PackageType
} from '../../../constants/inventory/InventoryTypes';
import * as EXPORT_TYPES from '../../../constants/inventory/InventoryExportTypes';
import * as PERMISSIONS from '../../../constants/permissions/inventory';
import ExportButton from '../../../containers/inventory/ExportCsvButton';
import AccessControl from '../../AccessControl';

const ExportTypes = {
  [ItemType]: EXPORT_TYPES.EXPORT_ITEMS,
  [PackageType]: EXPORT_TYPES.EXPORT_PACKAGES
};

const PermisionByType = {
  [ItemType]: PERMISSIONS.EXPORT_ITEMS_DETAILS,
  [PackageType]: PERMISSIONS.EXPORT_PACKAGES_DETAILS
};

const InventoryItemSearch = ({
  type,
  searchQuery,
  queryOptions,
  tagsOptions,
  placeholder,
  onChange,
  onSubmit,
  onClear,
  onInit,
  isFetching,
  isFetchingTagsOptions
}) => {
  useEffect(onInit, []);
  const handleOnChangeQuery = q => {
    onChange({ ...searchQuery, q });
  };
  const handleOnChangeCreatedAt = createdAt => {
    onChange({ ...searchQuery, createdAt });
  };
  const handleOnChangeTags = tagId => {
    onChange({ ...searchQuery, tagId });
  };
  const hasQuery = !every(isEmpty, searchQuery);
  return (
    <Grid fluid style={{ padding: 0, marginLeft: 0 }}>
      <Row gutter={8}>
        <Col xs={24} md={6} lg={5}>
          <Input
            value={getOr('', 'q', queryOptions)}
            onChange={handleOnChangeQuery}
            style={{ paddingLeft: 10 }}
            placeholder={placeholder}
            onKeyPress={({ key }) => {
              if (key === 'Enter') onSubmit(queryOptions);
            }}
          />
        </Col>
        <Col xs={24} md={6} lg={5}>
          <DateRangePicker
            style={{ width: '100%' }}
            value={getOr([], 'createdAt', queryOptions)}
            placeholder="Select date range"
            onChange={handleOnChangeCreatedAt}
          />
        </Col>
        <Col xs={24} md={6} lg={4}>
          <CheckPicker
            style={{ width: '100%' }}
            data={tagsOptions}
            value={queryOptions.tagId || []}
            placeholder={isFetchingTagsOptions ? 'Loading tags' : 'Select tags'}
            onChange={handleOnChangeTags}
          />
        </Col>
        <Col xs={24} md={24} lg={7}>
          <Button
            color="green"
            style={{ borderBottom: 'none', marginRight: 10 }}
            disabled={!hasQuery || isFetching}
            onClick={() => onSubmit(queryOptions)}
          >
            <Icon icon="search" /> Search
          </Button>
          <Button
            style={{ borderBottom: 'none', marginRight: 10 }}
            disabled={isFetching}
            onClick={onClear}
          >
            <Icon icon="refresh" /> Reset
          </Button>
          <AccessControl allowOnlyFor={viewAccess(PermisionByType[type])}>
            {!isFetching && (
              <ExportButton
                type={ExportTypes[type]}
                searchQuery={searchQuery}
              />
            )}
          </AccessControl>
        </Col>
      </Row>
    </Grid>
  );
};

const SearchQueryType = PropTypes.shape({
  q: PropTypes.string,
  createdAt: PropTypes.arrayOf(PropTypes.shape({})),
  tagId: PropTypes.arrayOf(PropTypes.number)
});

InventoryItemSearch.propTypes = {
  type: PropTypes.oneOf([ItemType, PackageType]).isRequired,
  placeholder: PropTypes.string.isRequired,
  searchQuery: SearchQueryType,
  queryOptions: SearchQueryType,
  tagsOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onInit: PropTypes.func,
  isFetching: PropTypes.bool.isRequired,
  isFetchingTagsOptions: PropTypes.bool.isRequired
};

InventoryItemSearch.defaultProps = {
  searchQuery: {},
  queryOptions: {},
  onInit: () => {}
};

export default InventoryItemSearch;
